import { supabase } from '@/integrations/supabase/client';

export interface PermissionError {
  code: 'FORBIDDEN' | 'UNAUTHORIZED' | 'NETWORK_ERROR';
  message: string;
  originalError?: any;
}

// Centralized permission error handler
export const handlePermissionError = (error: any): PermissionError => {
  // Check for common Supabase permission errors
  if (error?.code === 'PGRST116' || error?.message?.includes('RLS')) {
    return {
      code: 'FORBIDDEN',
      message: 'You do not have permission to perform this action.',
      originalError: error
    };
  }
  
  if (error?.status === 401) {
    return {
      code: 'UNAUTHORIZED',
      message: 'Your session has expired. Please log in again.',
      originalError: error
    };
  }
  
  if (error?.status === 403) {
    return {
      code: 'FORBIDDEN',
      message: 'You do not have permission to access this resource.',
      originalError: error
    };
  }
  
  // Network or other errors
  return {
    code: 'NETWORK_ERROR',
    message: 'Unable to complete request. Please try again.',
    originalError: error
  };
};

// Wrapper for Supabase operations with permission handling
export const withPermissionHandling = async <T>(
  operation: () => Promise<{ data: T; error: any }>
): Promise<T> => {
  try {
    const { data, error } = await operation();
    
    if (error) {
      const permissionError = handlePermissionError(error);
      throw permissionError;
    }
    
    return data;
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      // Already a PermissionError
      throw error;
    }
    
    // Wrap unknown errors
    const wrappedError = handlePermissionError(error);
    throw wrappedError;
  }
};

// Check if user has required permission for an operation
export const checkOperationPermission = async (
  module: string, 
  operation: 'view' | 'create' | 'edit' | 'delete'
): Promise<boolean> => {
  try {
    const { data: user } = await supabase.auth.getUser();
    if (!user.user) return false;

    // Get user role from profile
    const { data: profile } = await supabase
      .from('profiles')
      .select('role')
      .eq('user_id', user.user.id)
      .single();

    if (!profile) return false;

    // Admins always have access
    if (profile.role === 'admin') return true;

    // Check specific permissions based on module and operation
    const permissionKey = `${operation.toUpperCase()}_${module.toUpperCase()}`;
    
    // This would typically check against a permissions table or config
    // For now, using basic role-based logic
    switch (operation) {
      case 'view':
        return ['admin', 'manager', 'user'].includes(profile.role);
      case 'create':
      case 'edit':
        return ['admin', 'manager'].includes(profile.role);
      case 'delete':
        return profile.role === 'admin';
      default:
        return false;
    }
  } catch (error) {
    console.error('Permission check failed:', error);
    return false;
  }
};